import type { LocationContent } from "./types";

export const phoenix_seo: LocationContent = {
  "hero": "Phoenix lawyers need a website that explains their practice clearly before anyone requests a consultation. We start with accurate office details, honest practice descriptions and a working path to reach your staff. Family law intake stays separate from other matters, so a Phoenix caller reaches someone who can answer specific questions. Reporting links completed work with calls, forms and signed cases your office can verify.",
  "whyHeading": "SEO for lawyers in Phoenix",
  "why": [
    "A Phoenix firm already knows which matters its attorneys want more of. Our work starts there rather than a generic list of ranking targets. We check whether the site explains those services, whether forms reach the right desk and whether profile details match the office address. Family law pages get their own wording, reviewed by an attorney before anything publishes.",
    "Partners should see what a proposal actually covers. We separate technical repairs from new content and outreach, noting why each task matters for the practice you run. Expanding services beyond staff capacity creates extra inquiries without useful consultations, so monthly reviews track the kinds of calls received and add signed cases when intake can confirm the source."
  ],
  "marketHeading": "The Phoenix Legal Search Market",
  "market": [
    "Maricopa County, Mesa, Tempe, Glendale, Gilbert and Peoria raise a separate question from an address: whether the firm actually serves clients there or simply mentions the area. We describe that coverage honestly rather than suggesting extra branches. A firm with one verified office can still discuss the communities it draws clients from, provided the language matches what attorneys confirm. Nearby firms accept different matters, so comparisons depend on practice focus.",
    "Google Maps local pack placement can shift between a search near the office and one farther out, based on relevance, distance and listing quality. Accurate categories and a working phone number help a profile represent the practice that exists today. Reviews should reflect genuine client experience, answered without exposing private details. Google AI Overviews and ChatGPT sometimes summarize attorney information before a visit to the website; we check those summaries against approved facts."
  ],
  "marketLinks": [
    {
      "name": "Arizona",
      "url": "/lawyer-seo/arizona-seo"
    }
  ],
  "keywordHeading": "Legal services people search for in Phoenix",
  "keywordIntro": "Prospective clients often name their legal problem first. Service pages should help them decide whether to call.",
  "keywords": [
    "family law attorneys taking new consultations",
    "divorce lawyers handling local filings",
    "lawyers accepting nearby clients",
    "attorneys reviewing current legal matters",
    "law firms with an established office"
  ],
  "includedHeading": "What's included in a Phoenix engagement",
  "checklist": [
    "Confirm office details and local pack visibility stay accurate",
    "Review family law pages and the consultation route together",
    "Resolve site errors, broken links and outdated service pages",
    "Check directory listings match the current office information today",
    "Request genuine client feedback and prepare careful public replies",
    "Pair monthly reports with calls, forms and signed cases received"
  ],
  "practiceHeading": "SEO playbooks for every Phoenix practice area.",
  "practiceOrder": [
    "family-law-lawyer-seo",
    "personal-injury-lawyer-seo",
    "dui-dwi-lawyer-seo",
    "criminal-defense-lawyer-seo",
    "estate-planning-lawyer-seo",
    "immigration-lawyer-seo",
    "employment-law-lawyer-seo",
    "corporate-business-lawyer-seo",
    "workers-compensation-lawyer-seo"
  ],
  "focus": [
    {
      "heading": "Family Law SEO in Phoenix",
      "text": "Family law pages need their own route through the site rather than sharing wording meant for every legal matter. We separate the questions a prospective client asks before calling from details an attorney reviews after intake begins. Profile links and internal links should point to the same approved explanation of the practice. Your attorneys sign off on every description before it reaches a reader, and intake staff confirm the contact path still works as described for Phoenix families seeking guidance.",
      "url": "/practice-areas/family-law-lawyer-seo",
      "label": "Family Law SEO strategy"
    }
  ],
  "faqs": [
    {
      "q": "What does SEO cost for attorneys in Phoenix?",
      "a": "The $200/month starting plan suits many firms, though scope depends on office count and competition. We review current pages, profile accuracy and practice focus before recommending a tier. A proposal lists the actual services included, so partners can weigh cost against their own consultation goals."
    },
    {
      "q": "How long does local search progress take near Phoenix?",
      "a": "Local pack movement typically takes four to six months, with busier practice areas needing longer. Site condition, profile history and review volume affect the pace. Monthly checkpoints separate finished tasks from search changes we observe, so the firm can judge progress without a fixed ranking date."
    },
    {
      "q": "Which legal practices can you support for a firm?",
      "a": "Practice priorities follow the matters your attorneys currently accept and the capacity your staff has for new consultations. Family law is the current focus area. Any additional practice needs its own reviewed page and a clear contact route, with reporting that separates real inquiries from browsing."
    },
    {
      "q": "How do you handle Google Business Profile and Maps?",
      "a": "We start from the real office address, correct categories and a working phone number tied to the right page. Client feedback should reflect genuine experience, with replies avoiding private details. A broad service-area setting does not create another office, so Maps activity gets compared against calls received."
    },
    {
      "q": "Can AI search visibility help a law firm?",
      "a": "Google AI Overviews, ChatGPT and similar tools sometimes summarize firm details drawn from public sources. We check whether those summaries match attorney-approved descriptions and correct errors where found. Reporting treats any mention as a signal separate from referrals and consultations, since accurate content cannot force a citation."
    },
    {
      "q": "How is advertising-rule review handled for this office?",
      "a": "Pages are reviewed against Arizona Rules of Professional Conduct 7.1 to 7.3 before publishing. Attorneys confirm wording, disclosures and factual accuracy for each description. That process records what was approved and avoids outcome promises, certification claims or extra rule citations the firm has not authorized."
    },
    {
      "q": "What does a partner see in monthly reporting?",
      "a": "Reports connect finished tasks with rankings observed, site visits, calls and submitted forms. Intake records then show which contacts turned into consultations or signed cases where the source can be confirmed. Unclear sources stay labeled as such, rather than attributed to marketing effort without supporting evidence."
    },
    {
      "q": "How do you choose nearby areas to mention for this firm?",
      "a": "We start with the verified office and the communities attorneys can genuinely serve given current staffing. Maricopa County, Mesa, Tempe, Glendale, Gilbert and Peoria get mentioned only where that service is real. Existing links should clarify actual coverage, not suggest branch offices or interchangeable pages."
    }
  ],
  "nearbyHeading": "Lawyer & Law Firm SEO Near Phoenix",
  "nearbyLinks": [
    {
      "name": "Arizona",
      "url": "/lawyer-seo/arizona-seo"
    }
  ],
  "servicesText": "See how profile work, site repairs and practice pages fit together in our",
  "caseDirectoryText": "Browse documented work from other legal markets before setting priorities for your own Phoenix practice.",
  "finalText": "Request a review built around the matters your Phoenix attorneys accept, with a consultation path your intake team can track, measure and adjust as the practice grows.",
  "adRules": "Arizona Rules of Professional Conduct 7.1–7.3",
  "trustIntro": "Every Phoenix engagement is run by the same senior legal SEO team, under the same exclusivity and reporting standards.",
  "processNote": "Every page is reviewed against Arizona Rules of Professional Conduct 7.1–7.3.",
  "bostonAi": false,
  "source": {
    "Secondary": "seo for lawyers phoenix; attorney seo phoenix; seo company for attorneys in phoenix; lawyer seo services phoenix; seo for attorneys phoenix; seo agency for lawyers in phoenix",
    "Tertiary": "phoenix legal seo; legal seo services in phoenix",
    "Practice focus": "phoenix family law seo",
    "Searched nearby areas": "seo for law firms maricopa county; law firm seo maricopa county"
  }
};
